import React, { useEffect, useState } from 'react';
import { apiService, Facility } from '../services/api';
import { Card } from '../components/Card';
import { Building2, Plus, Trash2, Edit, Search, Tv, Calendar, CheckCircle } from 'lucide-react';
import DailyChecks from './DailyChecks';

const Facilities: React.FC = () => {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showControlCounts, setShowControlCounts] = useState(false);
  const [activeTab, setActiveTab] = useState<'facilities' | 'daily-work'>('facilities');

  useEffect(() => {
    const fetchFacilities = async () => {
      setLoading(true);
      try {
        const data = await apiService.getFacilities();
        setFacilities(data);
      } finally {
        setLoading(false);
      }
    };
    fetchFacilities();
  }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const newFacility = await apiService.createFacility({ name });
    
    // Verileri otomatik yenile
    const fetchFacilities = async () => {
      setLoading(true);
      try {
        const data = await apiService.getFacilities();
        setFacilities(data);
      } finally {
        setLoading(false);
      }
    };
    
    await fetchFacilities();
    setName('');
  };

  const handleDelete = async (id: number) => {
    try {
      await apiService.deleteFacility(id);
      // Verileri otomatik yenile
      const fetchFacilities = async () => {
        setLoading(true);
        try {
          const data = await apiService.getFacilities();
          setFacilities(data);
        } finally {
          setLoading(false);
        }
      };
      
      await fetchFacilities();
    } catch (error) {
      console.error('Tesis silinirken hata oluştu:', error);
    }
  };

  const handleEdit = (id: number) => {
    // Implement edit functionality
    console.log('Edit facility with ID:', id);
  };

  // Filter facilities based on search term
  const filteredFacilities = facilities.filter(facility =>
    facility?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderFacilitiesTab = () => (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Tesis Yönetimi
            </h1>
            <p className="text-gray-600 text-sm">Tesis takip ve yönetim sistemi</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowControlCounts(!showControlCounts)}
            className={`px-4 py-2 rounded-lg transition-all duration-200 ${
              showControlCounts
                ? 'bg-green-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm font-medium">
                {showControlCounts ? 'Kontrol Sayıları Gizle' : 'Kontrol Sayıları Göster'}
              </span>
            </div>
          </button>
        </div>
      </div>

      {/* Search and Add Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleAdd} className="flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tesis adı girin..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              required
            />
          </div>
          <button
            type="submit"
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            <span className="font-medium">Tesis Ekle</span>
          </button>
        </form>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Tesis ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          />
        </div>
      </div>

      {/* Facilities List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Tesisler yükleniyor...</p>
          </div>
        ) : (
          <div className="overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">Tesis Listesi</h3>
              <p className="text-sm text-gray-600">
                Toplam {filteredFacilities.length} tesis bulundu
              </p>
            </div>
            <div className="divide-y divide-gray-200">
              {filteredFacilities.length === 0 ? (
                <div className="p-8 text-center">
                  <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">Henüz tesis eklenmemiş</p>
                  <p className="text-gray-400 text-sm">Yukarıdaki formu kullanarak ilk tesisinizi ekleyin</p>
                </div>
              ) : (
                filteredFacilities.map((facility) => (
                  <div
                    key={facility.id}
                    className="px-6 py-4 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Building2 className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-800">
                            {facility.name}
                          </h4>
                                                     {showControlCounts && facility.control_count !== undefined && (
                             <p className="text-sm text-gray-600">
                               Kontrol Sayısı: {facility.control_count}
                             </p>
                           )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(facility.id)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                          title="Düzenle"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(facility.id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
                          title="Sil"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderDailyWorkTab = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-4 mb-6">
        <div className="p-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg">
          <Calendar className="h-8 w-8 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
            Günlük İş Programı
          </h1>
          <p className="text-gray-600 text-sm">Tesis bazlı günlük iş takibi ve kontrol sistemi</p>
        </div>
      </div>
      <DailyChecks />
    </div>
  );

  return (
    <div className="h-screen overflow-y-auto p-6 space-y-4">
      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('facilities')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'facilities'
                ? 'bg-blue-500 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Building2 className="h-5 w-5" />
              <span>Tesisler</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('daily-work')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'daily-work'
                ? 'bg-green-500 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Calendar className="h-5 w-5" />
              <span>Günlük İş Programı</span>
            </div>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'facilities' ? renderFacilitiesTab() : renderDailyWorkTab()}
    </div>
  );
};

export default Facilities; 